/**
 * A game against the bot, played entirely in the browser.
 *
 * No server, no account, no match record — the same reasoning as the tutorial. An
 * opponent that costs nothing to provide and cannot be affected by the site being busy is
 * a better opponent than one that queues behind everyone else's game, and it means the
 * one thing a new player most needs (someone to play) is available before they have
 * decided whether to sign up for anything.
 *
 * The game is its move log here too. Every position on screen is replayed from the log
 * through the same engine the server runs, so taking a move back is `slice`, browsing is
 * replaying fewer moves, and there is no second copy of the position to fall out of step.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  FORMATION_NAMES,
  formationInfo,
  type FormationName,
  formatMove,
  Game,
  type Move,
  type PlayerIndex,
  threatenedKings,
  type Variant,
} from '@gok/engine';
import { levelById, type LevelId, LEVELS } from '@gok/bot';

import { useBot } from '../bot/useBot.ts';
import { LIVE, useHistoryKeys } from '../game/history.ts';
import { type Selection, targetsFor } from '../game/selection.ts';
import { Board } from '../ui/Board.tsx';
import { MoveList } from '../ui/MoveList.tsx';
import { Reserve } from '../ui/Reserve.tsx';
import { Badge, Button, Field, Panel, Seat, Select } from '../ui/primitives.tsx';
import { LEARN_HREF } from './router.ts';

const OUTCOME: Record<string, string> = {
  checkmate: 'Checkmate',
  resignation: 'Resignation',
  drawn: 'Draw by agreement',
  repetition: 'Draw by repetition',
};

const STORAGE_KEY = 'gok.play';

interface Setup {
  readonly formation: FormationName;
  readonly radius: number;
  readonly level: LevelId;
  readonly seat: PlayerIndex;
}

const DEFAULT_SETUP: Setup = {
  formation: 'monarchy',
  radius: 4,
  level: 'steady',
  seat: 0,
};

/**
 * A variant for a local game.
 *
 * The clock fields are part of `Variant` because a match on the server always has one.
 * Nothing here runs it: a game against a bot that thinks for a second a move has no use
 * for a flag, and pretending otherwise would mean showing a clock that never falls.
 */
const variantOf = (setup: Setup): Variant => ({
  radius: Math.max(setup.radius, formationInfo(setup.formation).minRadius),
  formation: setup.formation,
  spawnsAvailable: 12,
  timeInitialMs: 600_000,
  timeIncrementMs: 5_000,
  stakes: 0,
});

function loadSetup(): Setup {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    if (!saved || typeof saved !== 'object') return DEFAULT_SETUP;
    // Read defensively: this is last week's shape as easily as this week's.
    const formation = FORMATION_NAMES.includes(saved.formation)
      ? saved.formation as FormationName
      : DEFAULT_SETUP.formation;
    return {
      formation,
      radius: Number.isInteger(saved.radius) ? saved.radius : DEFAULT_SETUP.radius,
      level: LEVELS.some((l) => l.id === saved.level)
        ? saved.level as LevelId
        : DEFAULT_SETUP.level,
      seat: saved.seat === 1 ? 1 : 0,
    };
  } catch {
    return DEFAULT_SETUP;
  }
}

export function Play() {
  const [form, setForm] = useState<Setup>(loadSetup);
  /** The settings the game in progress was started with, frozen until the next one. */
  const [started, setStarted] = useState<Setup>(form);
  const [moves, setMoves] = useState<Move[]>([]);
  const [ply, setPly] = useState<number>(LIVE);
  const [selection, setSelection] = useState<Selection>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // Destructured rather than kept as an object: `thinking` changes on every search, and a
  // handle that changed identity with it would re-run the effect below and abandon the
  // very reply it was waiting for.
  const { think, cancel, thinking } = useBot();
  const variant = useMemo(() => variantOf(started), [started]);
  const mySeat = started.seat;
  const botSeat = (mySeat ^ 1) as PlayerIndex;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    } catch {
      // A preference that cannot be remembered still applies for this visit.
    }
  }, [form]);

  // The whole game, replayed. Cheap enough to do on every render of a hundred-move game,
  // and it removes the entire class of bug where a cached board and a log disagree.
  const replay = useMemo(() => {
    const game = new Game(variant);
    const sans: { san: string }[] = [];
    for (const move of moves) {
      sans.push({ san: formatMove(game.position, move) });
      game.play(move);
    }
    return { game, sans };
  }, [variant, moves]);

  const live = ply >= moves.length;

  const shown = useMemo(() => {
    if (live) return { game: replay.game, lastMove: moves[moves.length - 1] };
    const game = new Game(variant);
    for (let i = 0; i < ply; i++) game.play(moves[i]);
    return { game, lastMove: moves[ply - 1] };
  }, [live, replay.game, variant, moves, ply]);

  const game = shown.game;
  const toMove = game.position.playerToMove;
  const myTurn = live && !game.isOver && toMove === mySeat;

  const legalMoves = useMemo(
    () => (myTurn ? replay.game.legalMoves() : []),
    [myTurn, replay.game],
  );

  const targets = useMemo(
    () => targetsFor(game.position, legalMoves, selection),
    [game, legalMoves, selection],
  );

  useEffect(() => setSelection(null), [moves.length, ply]);

  useHistoryKeys({ ply, moveCount: moves.length, seek: setPly });

  // The bot's turn. Keyed on the log rather than on the game object, because the game is
  // rebuilt on every render and would otherwise restart the search each time.
  const botTurn = live && !replay.game.isOver &&
    replay.game.position.playerToMove === botSeat;

  // No guard against asking twice: the effect's dependencies change exactly once per
  // move, and `useBot` drops the reply to any request that has been superseded. A guard
  // here would only be able to see that a request *was* sent, not whether its reply
  // survived — which is how a remount in the middle of the bot's turn leaves it frozen.
  useEffect(() => {
    if (!botTurn) return;

    let abandoned = false;
    think(variant, moves, form.level)
      .then((thought) => {
        if (abandoned) return;
        setFailure(null);
        setMoves((current) =>
          current.length === moves.length ? [...current, thought.move] : current
        );
      })
      .catch((error: Error) => {
        if (!abandoned) setFailure(error.message);
      });

    return () => {
      abandoned = true;
    };
  }, [botTurn, moves, variant, form.level, think]);

  const play = useCallback((move: Move) => {
    setSelection(null);
    // Checked rather than trusted. There is no server here to refuse a move that should
    // not have been offered, so the screen that owns the game has to be the one that
    // says no — and saying no quietly is much better than replaying a log that cannot be
    // replayed, which takes the whole board down with it.
    if (!replay.game.isLegal(move)) return;
    setMoves((current) => [...current, move]);
  }, [replay.game]);

  const newGame = useCallback(() => {
    cancel();
    setStarted(form);
    setMoves([]);
    setPly(LIVE);
    setSelection(null);
    setFailure(null);
  }, [cancel, form]);

  /**
   * Rewind to the last position where it was your turn.
   *
   * Not "drop two moves": if the bot is still thinking there is only one move to undo,
   * and dropping two would hand the turn straight back to it. Whose turn it is follows
   * from the length of the log, so that is what decides how far back to go.
   */
  const takeBack = useCallback(() => {
    cancel();
    setMoves((current) => {
      let back = current.length - 1;
      while (back > 0 && back % 2 !== mySeat) back--;
      return current.slice(0, Math.max(0, back));
    });
    setPly(LIVE);
    setFailure(null);
  }, [cancel, mySeat]);

  const level = levelById(form.level);
  const finished = replay.game.isOver;
  const won = finished && replay.game.winner === mySeat;

  return (
    <div className='flex h-full min-h-0 flex-col lg:flex-row'>
      {
        /* The board keeps a floor on a phone, and the panel beside it a ceiling. Without
          both, the move list grows to fit its text and squeezes the board — and unlike a
          transcript, a board that has to be squinted at cannot be scrolled. */
      }
      <div className='relative flex min-h-[40vh] flex-1 items-center justify-center p-2
          sm:p-4 lg:min-h-0'>
        {!live && (
          <button
            type='button'
            onClick={() => setPly(LIVE)}
            className='absolute top-3 left-1/2 z-10 -translate-x-1/2 rounded-full border
              border-alert bg-alert-soft px-3 py-1 text-xs font-medium text-alert shadow-sm'
          >
            Viewing move {ply} of {moves.length} — back to the game
          </button>
        )}

        <div className='h-full w-full'>
          <Board
            board={game.position.board}
            cells={game.position.cells}
            selection={selection}
            targets={targets}
            viewFrom={mySeat}
            toMove={toMove}
            interactive={myTurn}
            lastMove={shown.lastMove}
            threatened={threatenedKings(game.position)}
            onSelect={setSelection}
            onPlay={play}
          />
        </div>
      </div>

      <aside className='flex max-h-[48vh] w-full shrink-0 flex-col gap-2 overflow-y-auto
          border-t border-rule bg-ground p-2 lg:h-full lg:max-h-none lg:w-80 lg:gap-3
          lg:overflow-hidden lg:border-t-0 lg:border-l lg:p-3'>
        <PlayerRow
          name={level.label}
          detail={thinking ? 'thinking…' : ''}
          seat={botSeat}
          running={live && !finished && toMove === botSeat}
          spawns={replay.game.position.spawns[botSeat]}
        />

        {finished && (
          <div className='rounded-md border border-rule bg-surface px-3 py-2 text-sm'>
            <strong className='font-semibold'>
              {OUTCOME[replay.game.status] ?? replay.game.status}
            </strong>
            <div className='mt-0.5 text-ink-2'>
              {replay.game.winner === undefined
                ? 'A draw.'
                : won
                ? 'You win.'
                : `${level.label} wins.`}
            </div>
          </div>
        )}

        {failure && (
          <p className='rounded-md border border-alert bg-alert-soft px-3 py-2 text-sm text-alert'>
            The bot stopped: {failure}. Start a new game.
          </p>
        )}

        <Panel
          title='Moves'
          className='min-h-24 lg:min-h-40 lg:flex-1'
          actions={
            <span className='font-mono text-[0.7rem] text-muted'>
              {started.formation} · r{variant.radius} · untimed
            </span>
          }
        >
          <MoveList moves={replay.sans} ply={live ? -1 : ply} onSeek={setPly} />
        </Panel>

        <PlayerRow
          name='You'
          detail={myTurn ? 'your move' : finished ? '' : 'waiting'}
          seat={mySeat}
          running={myTurn}
          spawns={replay.game.position.spawns[mySeat]}
          onPickReserve={myTurn
            ? () =>
              setSelection((current) =>
                current?.kind === 'reserve' ? null : { kind: 'reserve' }
              )
            : undefined}
          reserveSelected={selection?.kind === 'reserve'}
        />

        <div className='flex flex-wrap gap-2'>
          <Button
            className='flex-1'
            disabled={!myTurn}
            onClick={() => play({ type: 'pass' })}
            title='Two passes in a row end the game as a draw'
          >
            Pass
          </Button>
          {
            /* Enabled after the game ends too: losing and wanting to see where it went
            wrong is the most likely moment to reach for it, and there is no opponent
            here who has to agree. */
          }
          <Button className='flex-1' disabled={moves.length === 0} onClick={takeBack}>
            Take back
          </Button>
          {!finished && moves.length > 0 && (
            <Button
              tone='danger'
              className='flex-1'
              onClick={() => play({ type: 'resign' })}
            >
              Resign
            </Button>
          )}
        </div>

        <NewGamePanel
          form={form}
          onChange={setForm}
          onStart={newGame}
          open={setupOpen}
          onToggle={() => setSetupOpen((v) => !v)}
          started={started}
        />
      </aside>
    </div>
  );
}

function PlayerRow({
  name,
  detail,
  seat,
  running,
  spawns,
  onPickReserve,
  reserveSelected = false,
}: {
  name: string;
  detail: string;
  seat: PlayerIndex;
  running: boolean;
  spawns: number;
  onPickReserve?: () => void;
  reserveSelected?: boolean;
}) {
  return (
    <div
      className={`rounded-md border bg-surface px-3 py-2 transition-colors
        ${running ? 'border-accent' : 'border-rule'}`}
    >
      <div className='flex items-center gap-2'>
        <Seat player={seat} />
        <span className='text-sm font-medium'>{name}</span>
        <span className='ml-auto truncate text-xs text-muted'>{detail}</span>
      </div>
      <div className='mt-1.5'>
        <Reserve
          count={spawns}
          player={seat}
          selected={reserveSelected}
          onPick={onPickReserve}
        />
      </div>
    </div>
  );
}

/**
 * The settings, and the button that applies them.
 *
 * Difficulty is the exception: it takes effect on the bot's next move rather than waiting
 * for a new game, because "this is too hard" is a thing you discover in the middle of a
 * game and should not have to abandon it to act on.
 */
function NewGamePanel({
  form,
  started,
  onChange,
  onStart,
  open,
  onToggle,
}: {
  form: Setup;
  started: Setup;
  onChange(setup: Setup): void;
  onStart(): void;
  open: boolean;
  onToggle(): void;
}) {
  const minRadius = formationInfo(form.formation).minRadius;
  const radii = [2, 3, 4, 5, 6].filter((r) => r >= minRadius);
  const pending = form.formation !== started.formation ||
    Math.max(form.radius, minRadius) !==
      Math.max(started.radius, formationInfo(started.formation).minRadius) ||
    form.seat !== started.seat;

  return (
    <Panel
      title='Setup'
      className='shrink-0'
      actions={
        <button
          type='button'
          onClick={onToggle}
          className='font-mono text-[0.7rem] text-muted hover:text-ink'
        >
          {open ? 'hide' : 'change'}
        </button>
      }
    >
      <div className='flex flex-col gap-3 p-3'>
        <Field label='Difficulty'>
          <Select
            value={form.level}
            onChange={(e) => onChange({ ...form, level: e.target.value as LevelId })}
          >
            {LEVELS.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </Select>
        </Field>
        <p className='-mt-1 text-xs text-muted'>{levelById(form.level).blurb}</p>

        {open && (
          <>
            <Field label='Formation'>
              <Select
                value={form.formation}
                onChange={(e) =>
                  onChange({
                    ...form,
                    formation: e.target.value as FormationName,
                  })}
              >
                {FORMATION_NAMES.map((name) => (
                  <option key={name} value={name}>
                    {name} · {formationInfo(name).kingsPerPlayer}{' '}
                    king{formationInfo(name).kingsPerPlayer > 1 ? 's' : ''}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label='Board'>
              <Select
                value={Math.max(form.radius, minRadius)}
                onChange={(e) => onChange({ ...form, radius: Number(e.target.value) })}
              >
                {radii.map((r) => <option key={r} value={r}>radius {r}</option>)}
              </Select>
            </Field>

            <Field label='You play'>
              <Select
                value={form.seat}
                onChange={(e) =>
                  onChange({ ...form, seat: Number(e.target.value) as PlayerIndex })}
              >
                <option value={0}>first</option>
                <option value={1}>second</option>
              </Select>
            </Field>
          </>
        )}

        <div className='flex items-center gap-2'>
          <Button
            tone={pending ? 'primary' : 'quiet'}
            onClick={onStart}
            className='flex-1'
          >
            New game
          </Button>
          {pending && <Badge tone='accent'>unapplied</Badge>}
        </div>

        <p className='text-xs text-muted'>
          Played in this browser and nowhere else — no account, no clock, and it keeps
          working with the site offline.{' '}
          <a href={LEARN_HREF} className='text-accent hover:underline'>
            New to the game?
          </a>
        </p>
      </div>
    </Panel>
  );
}
